const API_URL = process.env.REACT_APP_API_URL || "https://api.oltodo.com/api";

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}`,
});

// Rafraîchit l'access token (expire au bout de 15 min côté serveur, voir
// OLTODO-backend/src/utils/jwt.ts) via le refresh token — jusqu'ici totalement absent de ce
// fichier, ce qui donnait l'impression que "le panel n'affiche plus rien après quelques
// secondes" : chaque requête après expiration recevait un 401 jamais détecté (aucune méthode
// ne vérifiait res.ok), donc un payload d'erreur silencieusement rendu comme une liste vide.
// Port fidèle du mécanisme déjà éprouvé dans oltodo-web/src/utils/api.ts (promesse mutualisée
// entre requêtes concurrentes, un seul appel à /auth/refresh, retry une fois).
let refreshPromise: Promise<string | null> | null = null;
let onSessionExpired: (() => void) | null = null;
export const setOnSessionExpired = (fn: (() => void) | null) => { onSessionExpired = fn; };

const refreshAccessToken = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const rt = localStorage.getItem("admin_refresh_token");
    if (!rt) return null;
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) throw new Error("refresh failed");
      const data = await res.json();
      if (data.accessToken) localStorage.setItem("admin_token", data.accessToken);
      if (data.refreshToken) localStorage.setItem("admin_refresh_token", data.refreshToken);
      return data.accessToken || null;
    } catch {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_refresh_token");
      localStorage.removeItem("admin_user");
      onSessionExpired?.();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

const authFetch = async (path: string, options: RequestInit = {}) => {
  let res = await fetch(`${API_URL}${path}`, { ...options, headers: { ...getHeaders(), ...(options.headers || {}) } });
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await fetch(`${API_URL}${path}`, { ...options, headers: { ...getHeaders(), ...(options.headers || {}) } });
    }
  }
  return res;
};

// Jusqu'ici res.ok n'était jamais vérifié : une 403 (rôle insuffisant) ou une 500 revenait
// comme un JSON `{error:...}` traité comme une réponse normale — d'où des pages qui semblaient
// juste vides plutôt que de montrer un vrai message d'erreur.
const parseJsonSafe = async (res: Response) => {
  const text = await res.text();
  try {
    const data = text ? JSON.parse(text) : {};
    if (!res.ok && !data.error) data.error = `Erreur serveur (${res.status})`;
    return data;
  } catch {
    return { error: res.ok ? "Réponse invalide du serveur" : `Erreur serveur (${res.status})` };
  }
};

export const api = {
  get: (path: string) => authFetch(path).then(parseJsonSafe),
  post: (path: string, body: any) =>
    authFetch(path, { method: "POST", body: JSON.stringify(body) }).then(parseJsonSafe),
  put: (path: string, body: any) =>
    authFetch(path, { method: "PUT", body: JSON.stringify(body) }).then(parseJsonSafe),
  delete: (path: string) =>
    authFetch(path, { method: "DELETE" }).then(parseJsonSafe),
};

export default API_URL;
